import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Button,
  Alert,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useDarkTimeWarning } from '../../components/forms/observation/hooks/useDarkTimeWarning';
import DarkTimeWarningAlert from '../../components/observations/DarkTimeWarningAlert';
import { warningMessages } from '../../utils/darkTimeWarning';
import { toExposureCount } from '../../utils/exposureCount';
import { apiService } from '../../services/apiService';
import ProjectStep from '../../components/forms/observation/ProjectStep';
import UnifiedTargetStep from '../../components/forms/observation/UnifiedTargetStep';
import ConstraintsStep from '../../components/forms/observation/ConstraintsStep';
import AutofocusStep from '../../components/forms/observation/AutofocusStep';
import GuidingStep from '../../components/forms/observation/GuidingStep';
import RepetitionStep from '../../components/forms/observation/RepetitionStep';
import ReviewStep from '../../components/forms/observation/ReviewStep';
import ConflictResolutionDialog from '../../components/ConflictResolutionDialog';
import { MOON_PEAK_DEFAULT, MOON_WIDTH_DEFAULT } from '../../constants/moon';

const steps = ['Project', 'Target & Exposure', 'Constraints', 'Repetition', 'Autofocus', 'Guiding', 'Review & Submit'];

const ObservationSubmit = ({ embedded, onSuccess }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [, setLoadingFilters] = useState(true);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [availableReadoutModes, setAvailableReadoutModes] = useState([]);
  const [defaultReadoutMode, setDefaultReadoutMode] = useState(null);
  const [observatoriesWithInstruments, setObservatoriesWithInstruments] = useState([]);
  const [observatoryLocation, setObservatoryLocation] = useState(null);
  const [allObservatories, setAllObservatories] = useState([]); // Store all observatories for filter lookup

  // #1119: dark-time advisories returned by the create. Non-empty holds navigation until acknowledged.
  const [darkTimeWarnings, setDarkTimeWarnings] = useState([]);

  // Conflict resolution state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingObservationData, setPendingObservationData] = useState(null);
  
  const methods = useForm({
    defaultValues: {
      // Observation details
      observationName: '',

      // Project selection
      projectId: '',

      // Observatory assignment
      assignedTo: '',

      // Target information
      targetName: '',
      ra: '',
      dec: '',
      epoch: 'J2000',
      priority: 5,
      positionAngle: 0,
      
      // Multi-target support
      targets: [],
      
      // Exposure configurations (for single target mode)
      sequenceRepeatCount: 1,
      exposures: [
        {
          filter: 'L',
          exposureTime: 300,
          exposureCount: 1,
          binning: '1',
          gain: null,
          offset: null,
        }
      ],
      
      // Constraints
      minAltitude: 30,
      maxAirmass: 2.0,
      // #1157: moonSeparation is the requirement AT FULL MOON (the Lorentzian peak), collected
      // directly rather than back-solved from an anchor day -- that back-solve inflated the
      // stored value (60 deg at day 10 became a peak of 115 deg) and could exceed the 180 deg
      // maximum possible separation, silently making a target unschedulable near full moon.
      moonSeparation: MOON_PEAK_DEFAULT,
      moonAvoidanceWidth: MOON_WIDTH_DEFAULT,
      moonDown: false,
      startTime: null,
      endTime: null,
      
      // Additional options
      notes: '',
      guidingEnabled: true,        // !disable_guiding
      requireGuiding: false,       // force_guiding
      ditherEnabled: true,         // dither_enabled (default true: NINA convention)
      
      // Autofocus settings
      afEnabled: true,
      initialAutofocus: true,
      afTimeBased: true,
      afTimeInterval: 30,
      afTempBased: false,
      afTempThreshold: 2.0,
      afHfrBased: false,
      afHfrPercentage: 5,
      afFilterChange: false,
      afAfterExposures: false,
      afAfterExposuresCount: 10,
      // Legacy fields kept for backward compat
      autofocusInterval: 30,
      temperatureCompensation: false,
      temperatureThreshold: 1.0,
      
      // Removed global instrument selection - handled per-filter
      
      // Fixed-time observation fields
      observationType: 'flexible',
      fixedTimeStart: null,
      fixedTimeEnd: null,
      // #937: UI-only. 'exposures' = pinned start, window auto-sized from the duration estimate.
      fixedTimeMode: 'exposures',
      startToleranceMinutes: 0,

      // Time-based observation fields
      minimumObservingTimeMinutes: null,
      desiredObservingTimeMinutes: null,
      fillTime: false,  // For time_based: fill allocated time with unlimited exposures

      // Monitoring cadence fields (deprecated - kept for backward compatibility)
      cadenceDays: 1,
      cadenceStartDate: null,
      cadenceEndDate: null,
      cadenceMaxObservations: null,

      // Repetitive observation fields
      isRepetitive: false,
      repetitionStartDate: null,
      repetitionEndDate: null,
      repetitionIntervalDays: 0,
      repetitionIntervalHours: 0,
      repetitionMaxExecutions: null,
      repetitionWindowHours: 2,
      repetitionSkipIfDelayedHours: null,
      repetitionPriorityBoostPerDay: 0,

      // Calibration options (null/empty = inherit from user/observatory/system)
      calibrationEnabled: '',
      rotationMatchFlats: '',
    }
  });

  // Helper function to convert filter names to options with colors/labels
  const createFilterOptions = (filters) => {
    const colorMap = {
      'L': '#ffffff',
      'R': '#ff0000',
      'G': '#00ff00',
      'B': '#0000ff',
      'Ha': '#ff6b6b',
      'HA': '#ff6b6b',
      'OIII': '#4ecdc4',
      'SII': '#ffe66d',
      'Clear': '#e0e0e0',
      'C': '#e0e0e0',
      'V': '#90ee90',
      'I': '#8b0000',
      'CBB': '#1e90ff',
    };

    const labelMap = {
      'L': 'Luminance (L)',
      'R': 'Red (R)',
      'G': 'Green (G)',
      'B': 'Blue (B)',
      'Ha': 'H-alpha (Ha)',
      'HA': 'H-alpha (HA)',
      'OIII': 'Oxygen III (OIII)',
      'SII': 'Sulfur II (SII)',
      'Clear': 'Clear/No Filter',
      'C': 'Clear (C)',
      'V': 'Visual (V)',
      'I': 'Infrared (I)',
      'CBB': 'Clear Blue Blocking (CBB)',
    };

    return filters.sort().map(filter => ({
      value: filter,
      label: labelMap[filter] || filter,
      color: colorMap[filter] || '#999999'
    }));
  };

  // Check for prefill data from Target Library
  useEffect(() => {
    const prefillData = sessionStorage.getItem('prefillObservation') || localStorage.getItem('prefillObservation');
    if (prefillData) {
      try {
        const data = JSON.parse(prefillData);
        // Clear both storage locations so it doesn't persist
        sessionStorage.removeItem('prefillObservation');
        localStorage.removeItem('prefillObservation');

        // Prefill observatory assignment
        if (data.assignedTo) {
          methods.setValue('assignedTo', data.assignedTo);
        }

        // Prefill single-target fields
        if (data.observationName) methods.setValue('observationName', data.observationName);
        if (data.name) methods.setValue('name', data.name);
        if (data.ra) methods.setValue('ra', data.ra);
        if (data.dec) methods.setValue('dec', data.dec);
        if (data.positionAngle) methods.setValue('positionAngle', data.positionAngle);
        if (data.sequenceRepeatCount) methods.setValue('sequenceRepeatCount', data.sequenceRepeatCount);
        if (data.exposures && data.exposures.length > 0) {
          methods.setValue('exposures', data.exposures);
        }

        // Prefill multi-target data
        if (data.targets && data.targets.length > 0) {
          methods.setValue('targets', data.targets);
          // Explicitly set nested exposureConfigs after field arrays initialize
          setTimeout(() => {
            data.targets.forEach((t, idx) => {
              if (t.exposureConfigs && t.exposureConfigs.length > 0) {
                methods.setValue(`targets.${idx}.exposureConfigs`, t.exposureConfigs);
              }
            });
          }, 200);
        }
        if (data.minAltitude) methods.setValue('minAltitude', data.minAltitude);
        if (data.maxAirmass) methods.setValue('maxAirmass', data.maxAirmass);
        if (data.moonSeparation) methods.setValue('moonSeparation', data.moonSeparation);
        if (data.moonAvoidanceWidth) methods.setValue('moonAvoidanceWidth', data.moonAvoidanceWidth);
        if (data.moonDown !== undefined) methods.setValue('moonDown', data.moonDown);
        if (data.guidingEnabled !== undefined) methods.setValue('guidingEnabled', data.guidingEnabled);
        if (data.requireGuiding !== undefined) methods.setValue('requireGuiding', data.requireGuiding);
        if (data.ditherEnabled !== undefined) methods.setValue('ditherEnabled', data.ditherEnabled);
        if (data.initialAutofocus !== undefined) methods.setValue('initialAutofocus', data.initialAutofocus);
        if (data.autofocusInterval) methods.setValue('autofocusInterval', data.autofocusInterval);
        if (data.useDefaults !== undefined) methods.setValue('useDefaults', data.useDefaults);
        if (data.isRepetitive) {
          methods.setValue('isRepetitive', true);
          methods.setValue('repetitionIntervalDays', data.repetitionIntervalDays || 0);
          methods.setValue('repetitionIntervalHours', data.repetitionIntervalHours || 0);
          methods.setValue('repetitionWindowHours', data.repetitionWindowHours || 2);
          methods.setValue('repetitionPriorityBoostPerDay', data.repetitionPriorityBoostPerDay || 0);
        }

        // Observation type and priority
        if (data.observationType) methods.setValue('observationType', data.observationType);
        if (data.priority) methods.setValue('priority', data.priority);

        // Store project name for later lookup (will be resolved in ProjectStep)
        if (data.projectName) {
          sessionStorage.setItem('prefillProjectName', data.projectName);
        }

        // Jump to specific step if requested
        if (data.initialStep != null && typeof setActiveStep === 'function') {
          setTimeout(() => setActiveStep(data.initialStep), 100);
        }

        console.log('Pre-filled observation form:', data);
      } catch (err) {
        console.error('Failed to parse prefill data:', err);
      }
    }
  }, [methods]);

  // Load observatories on mount
  useEffect(() => {
    const loadObservatories = async () => {
      try {
        const response = await apiService.getObservatories();
        const observatories = response.data.observatories;
        setAllObservatories(observatories);

        // Collect instruments from observatories
        const obsWithInstruments = [];
        observatories.forEach(observatory => {
          if (observatory.capabilities?.instrument_selector &&
              observatory.capabilities?.available_instruments?.length > 0) {
            obsWithInstruments.push({
              id: observatory._id,
              name: observatory.name,
              instruments: observatory.capabilities.available_instruments
            });
          }
        });
        setObservatoriesWithInstruments(obsWithInstruments);

        // Set first observatory location for rise/set calculations
        const firstWithLocation = observatories.find(obs => obs.location);
        if (firstWithLocation) {
          setObservatoryLocation({
            latitude: firstWithLocation.location.latitude,
            longitude: firstWithLocation.location.longitude,
            altitude: firstWithLocation.location.altitude_meters || firstWithLocation.location.altitude || 0,
            timezone: firstWithLocation.location.timezone || 'UTC'
          });
        }

        // Check if an observatory is already selected (e.g., auto-selected by ProjectStep)
        // and narrow filters to that observatory. Otherwise show defaults until one is selected.
        const currentAssignedTo = methods.getValues('assignedTo');
        if (currentAssignedTo) {
          const selectedObs = observatories.find(obs => obs._id === currentAssignedTo);
          if (selectedObs?.capabilities?.available_filters?.length > 0) {
            setAvailableFilters(createFilterOptions(selectedObs.capabilities.available_filters));
          }
        }
      } catch (error) {
        console.error('Failed to load observatories:', error);
        setAvailableFilters([
          { value: 'L', label: 'Luminance (L)', color: '#ffffff' },
          { value: 'R', label: 'Red (R)', color: '#ff0000' },
          { value: 'G', label: 'Green (G)', color: '#00ff00' },
          { value: 'B', label: 'Blue (B)', color: '#0000ff' },
          { value: 'Ha', label: 'H-alpha (Ha)', color: '#ff6b6b' },
          { value: 'OIII', label: 'Oxygen III (OIII)', color: '#4ecdc4' },
          { value: 'SII', label: 'Sulfur II (SII)', color: '#ffe66d' },
          { value: 'Clear', label: 'Clear/No Filter', color: '#e0e0e0' },
        ]);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadObservatories();
  }, []);

  // Helper to update filters, readout modes, and location for a selected observatory
  const updateForObservatory = (observatory) => {
    if (!observatory) return;
    const filters = observatory.capabilities?.available_filters || [];
    if (filters.length > 0) {
      setAvailableFilters(createFilterOptions(filters));
    }
    // Prefer the durable capabilities list (persisted across plugin disconnects, #706);
    // fall back to live equipment_status for older observatory documents that haven't
    // had a telemetry-driven sync since the field was added.
    const readoutModes =
      observatory.capabilities?.available_readout_modes
      || observatory.equipment_status?.camera?.readout_modes
      || [];
    setAvailableReadoutModes(readoutModes);

    // Pre-fill source for new exposure_configs: the operator-set per-camera
    // default_readout_mode on the observatory, if any. Look up by the same
    // camera key the calibration system uses (camera_name + sensor dimensions).
    // Issue: MP Oersted readout-mode drift, 2026-05-30.
    const cameraCfg = observatory.equipment_configuration?.camera;
    const cameraKey = cameraCfg?.name
      ? `${cameraCfg.name}_${cameraCfg.sensor_width_px ?? ''}_${cameraCfg.sensor_height_px ?? ''}`
      : null;
    const cameraConfigs = observatory.configuration?.calibration_settings?.camera_configs || {};
    setDefaultReadoutMode((cameraKey && cameraConfigs[cameraKey]?.default_readout_mode) || null);
    if (observatory.location) {
      setObservatoryLocation({
        latitude: observatory.location.latitude,
        longitude: observatory.location.longitude,
        altitude: observatory.location.altitude_meters || observatory.location.altitude || 0,
        timezone: observatory.location.timezone || 'UTC'
      });
    }
  };

  // Update available filters when observatory selection changes
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'assignedTo' && value.assignedTo && allObservatories.length > 0) {
        const selectedObservatory = allObservatories.find(obs => obs._id === value.assignedTo);
        updateForObservatory(selectedObservatory);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, allObservatories]);

  // When allObservatories loads, check if an observatory is already selected
  // (handles race condition where ProjectStep auto-selects before observatories load)
  useEffect(() => {
    if (allObservatories.length > 0) {
      const currentAssignedTo = methods.getValues('assignedTo');
      if (currentAssignedTo) {
        const selectedObservatory = allObservatories.find(obs => obs._id === currentAssignedTo);
        updateForObservatory(selectedObservatory);
      }
    }
  }, [allObservatories]);

  // Auto-enable fill_time for rise-to-set observations (Issue #155)
  // Rise-to-set observations should always fill available time by definition
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'observationType' && value.observationType === 'rise_to_set') {
        methods.setValue('fillTime', true);
        console.log('Auto-enabled fillTime for rise-to-set observation');
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Helper function to convert RA from HH:MM:SS to decimal degrees
  const convertRaToDecimal = (raString) => {
    if (!raString) return 0;
    
    // If already a number, return it
    if (!isNaN(raString)) return parseFloat(raString);
    
    // Convert from HH:MM:SS
    const parts = raString.split(/[:\s]+/);
    if (parts.length === 3) {
      const hours = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return (hours + minutes/60 + seconds/3600) * 15; // Convert hours to degrees
    }
    return parseFloat(raString);
  };

  // Helper function to convert Dec from DD:MM:SS to decimal degrees
  const convertDecToDecimal = (decString) => {
    if (!decString) return 0;
    
    // If already a number, return it
    if (!isNaN(decString)) return parseFloat(decString);
    
    // Convert from DD:MM:SS
    const parts = decString.split(/[:\s]+/);
    if (parts.length === 3) {
      const sign = decString.includes('-') ? -1 : 1;
      const degrees = Math.abs(parseFloat(parts[0]));
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return sign * (degrees + minutes/60 + seconds/3600);
    }
    return parseFloat(decString);
  };

  // #1119: live dark-time advisory while the window is being built, so the user hears about a
  // truncation before committing to it rather than after the observation exists.
  const liveDarkTimeWarning = useDarkTimeWarning({
    observatoryId: methods.watch('assignedTo'),
    fixedTimeStart: methods.watch('fixedTimeStart'),
    fixedTimeEnd: methods.watch('fixedTimeEnd'),
    enabled: methods.watch('observationType') === 'fixed_time',
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    setError('');
    setDarkTimeWarnings([]);

    console.log('Form data being submitted:', JSON.stringify(data, null, 2));

    let observationData; // Declare outside try block so it's accessible in catch for conflict handling
    let createResult;    // #1119: the create response, so its warnings[] can be surfaced

    try {
      // Build targets array - always use array structure
      let targets = [];
      
      if (data.targets && data.targets.length > 0) {
        // Use targets from unified target step
        targets = data.targets;
      } else {
        setError('Please enter at least one target with coordinates');
        setSubmitting(false);
        return;
      }
      
      // Generate observation name if not provided
      let observationName = data.observationName;
      if (!observationName || observationName.trim() === '') {
        if (targets.length === 1) {
          // Single target: use target name
          observationName = targets[0].name;
        } else if (targets.length > 1) {
          // Multiple targets: create a descriptive name
          const targetNames = targets.slice(0, 3).map(t => t.name).join(', ');
          if (targets.length > 3) {
            observationName = `${targetNames}, and ${targets.length - 3} more`;
          } else {
            observationName = targetNames;
          }
        }
      }
      
      // Calculate observation priority from targets (use highest priority = highest number)
      const observationPriority = targets.length > 0
        ? Math.max(...targets.map(t => t.priority || data.priority || 5))
        : data.priority || 5;

      // Transform form data to API format
      observationData = {
        name: observationName || 'Unnamed Observation',
        project_id: data.projectId,
        observation_type: data.observationType,
        priority: observationPriority,
        global_repeat_count: data.globalRepeatCount || 1, // New field for cycling through all targets
        assigned_to: data.assignedTo || undefined // Observatory assignment (undefined allows backend to auto-assign)
      };
      
      // Process targets array - always use consistent structure
      observationData.targets = targets.map((t, index) => ({
        id: `target_${Date.now()}_${index}`,
        name: t.name,
        ...(t.naif_id ? { naif_id: t.naif_id } : {}),   // #1409: spacecraft NAIF id
        ra: convertRaToDecimal(t.ra),
        dec: convertDecToDecimal(t.dec),
        epoch: t.epoch || 'J2000',
        priority: t.priority || data.priority || 5,
        exposure_configs: (t.exposureConfigs && t.exposureConfigs.length > 0) 
          ? t.exposureConfigs.map(exp => ({
              filter: exp.filter || 'L',
              exposure_time: Number(exp.exposureTime) || 300,
              exposure_count: toExposureCount(exp.exposureCount),
              binning: parseInt(exp.binning || '1'),
              gain: exp.gain != null && exp.gain !== '' ? Number(exp.gain) : null,
              offset: exp.offset != null && exp.offset !== '' ? Number(exp.offset) : null,
              readout_mode: exp.readoutMode || null
            }))
          : [{
              // Default exposure if none defined
              filter: 'L',
              exposure_time: 300,
              exposure_count: 1,
              binning: 1,
              gain: null,
              offset: null,
              readout_mode: null
            }],
        sequence_repeat_count: t.sequenceRepeatCount || 1
      }));
      
      // Add constraints (same for all targets)
      observationData.constraints = {
        minimum_altitude: data.minAltitude || 30,
        maximum_airmass: data.maxAirmass || 2.0,
        // #1157: the requirement at full moon (Lorentzian peak), plus its half-width and the
        // moon-below-horizon override. Previously only moon_separation was sent, and in the
        // wizard's default (Lorentzian) mode its control was not even rendered -- so whatever the
        // user typed was discarded and a hardcoded 45 was saved.
        moon_separation: data.moonSeparation ?? 60,
        moon_avoidance_width: data.moonAvoidanceWidth ?? 5,
        moon_down: data.moonDown ?? false,
        maximum_moon_phase: data.maxMoonPhase || 100,
        maximum_clouds: data.maxClouds || 30,
        twilight_type: data.twilightType || 'nautical',
        start_time: data.startTime?.toISOString(),
        end_time: data.endTime?.toISOString(),
      };
      
      // Add metadata
      observationData.metadata = {
        notes: data.notes,
        is_fast_mover: data.isFastMover || false,
      };

      // Include source target IDs from library for duplicate prevention with automation
      // This allows the automation service to detect manual submissions
      const sourceTargetIds = targets
        .filter(t => t.savedTargetId)
        .map(t => t.savedTargetId);

      if (sourceTargetIds.length === 1) {
        // Single target from library - use singular field
        observationData.metadata.source_target_id = sourceTargetIds[0];
      } else if (sourceTargetIds.length > 1) {
        // Multiple targets from library - use array field
        observationData.metadata.source_target_ids = sourceTargetIds;
      }
      
      // Common fields for both single and multi-target observations
      // Plugin-recognized field names (issue #673). Server forwards verbatim.
      observationData.guiding_options = {
        disable_guiding: data.guidingEnabled === false,
        force_guiding: data.requireGuiding === true,
        dither_enabled: data.ditherEnabled !== false,
      };
      // Calculate estimated duration from the actual targets (not the legacy data.exposures field)
      // Overhead budget (TODO: make configurable per observatory, learn from actual telemetry):
      //   Fixed: slew/center (~7 min) + initial autofocus (~8 min) = 15 min
      //   Per-exposure: download (~7s) + dither settle (~10s) = ~17s
      //   Per-filter-change: filter wheel move + settle (~15s)
      //   Periodic autofocus: ~8 min every 30 min of imaging
      let totalShutterSeconds = 0;
      let totalExposures = 0;
      let totalFilterChanges = 0;
      for (const t of observationData.targets) {
        const repeat = t.sequence_repeat_count || 1;
        let lastFilter = null;
        for (const ec of t.exposure_configs) {
          const count = (ec.exposure_count || 1) * repeat;
          totalShutterSeconds += (ec.exposure_time || 0) * count;
          totalExposures += count;
          if (ec.filter !== lastFilter) {
            totalFilterChanges++;
            lastFilter = ec.filter;
          }
        }
      }
      const globalRepeat = data.globalRepeatCount || 1;
      totalShutterSeconds *= globalRepeat;
      totalExposures *= globalRepeat;
      totalFilterChanges *= globalRepeat;

      const fixedOverheadMin = 15; // slew/center (~7 min) + initial autofocus (~8 min)
      const perExposureOverheadSec = 17; // download (~7s) + dither settle (~10s)
      const perFilterChangeSec = 15;
      const shutterMin = totalShutterSeconds / 60;
      const periodicAfCount = Math.floor(shutterMin / 30); // autofocus every ~30 min
      const periodicAfMin = periodicAfCount * 8;
      const overheadMin = fixedOverheadMin
        + (totalExposures * perExposureOverheadSec) / 60
        + (totalFilterChanges * perFilterChangeSec) / 60
        + periodicAfMin;

      // #734: for flexible observations the SERVER computes the authoritative duration from
      // measured per-observatory capture cadence; don't send this rough client estimate (the
      // flat 15-min overhead over-allocated ~1.44x). Other types still send it.
      if (data.observationType !== 'flexible') {
        observationData.estimated_duration_minutes = Math.ceil(shutterMin + overheadMin);
      }
      
      // Add autofocus options - if using defaults, send null values so server uses observatory settings
      // If custom, send per-observation trigger overrides
      if (data.useDefaults) {
        observationData.autofocus_options = {};
      } else {
        observationData.autofocus_options = {
          enabled: data.afEnabled,
          initial_autofocus: data.initialAutofocus,
          trigger_overrides: {
            time_based: { enabled: data.afTimeBased, interval_minutes: parseInt(data.afTimeInterval) || 30 },
            temperature_based: { enabled: data.afTempBased, threshold_celsius: parseFloat(data.afTempThreshold) || 2.0 },
            hfr_based: { enabled: data.afHfrBased, increase_percentage: parseInt(data.afHfrPercentage) || 5 },
            filter_change: { enabled: data.afFilterChange },
            after_exposures: { enabled: data.afAfterExposures, exposure_count: parseInt(data.afAfterExposuresCount) || 10 },
          },
          // Legacy field for backward compat
          interval_minutes: data.afTimeBased ? (parseInt(data.afTimeInterval) || 30) : null
        };
      }
      
      // Removed global instrument selection - now handled per-filter in exposure_configs
      
      // Add fixed-time fields if applicable
      if (data.observationType === 'fixed_time') {
        // #937: only a start is required. An end time is a deadline, and only "fill the window"
        // genuinely needs one. Omit it and the server sizes the window from the duration estimate.
        if (!data.fixedTimeStart) {
          setError('Fixed-time observations require a start time');
          window.scrollTo(0, 0);
          return;
        }
        // `|| data.fillTime === true` is load-bearing: a rise_to_set observation is converted to
        // fixed_time + fillTime:true in ReviewStep and never touches fixedTimeMode. Reading the mode
        // alone would silently turn it count-based.
        const fillTime = data.fixedTimeMode === 'fill' || data.fillTime === true;
        if (fillTime && !data.fixedTimeEnd) {
          setError('"Fill the window" observations require an end time');
          window.scrollTo(0, 0);
          return;
        }
        observationData.fixed_time_start = data.fixedTimeStart.toISOString();
        observationData.fixed_time_end = data.fixedTimeEnd ? data.fixedTimeEnd.toISOString() : null;
        observationData.fill_time = fillTime;
        observationData.start_tolerance_minutes = Number(data.startToleranceMinutes) || 0;
      }

      // Add rise-to-set fields if applicable
      if (data.observationType === 'rise_to_set') {
        if (!data.riseToSetDate) {
          setError('Rise-to-set observations require a date');
          window.scrollTo(0, 0);
          return;
        }
        // Pass rise-to-set configuration to server
        observationData.riseToSetDate = data.riseToSetDate.toISOString();
        observationData.riseToSetMinAltitude = data.riseToSetMinAltitude || 30;
        observationData.riseToSetTwilightType = data.riseToSetTwilightType || 'nautical';
        observationData.riseToSetSafetyMargin = data.riseToSetSafetyMargin || 0;
      }

      // Add time-based observation fields if applicable
      if (data.observationType === 'time_based') {
        if (!data.minimumObservingTimeMinutes || !data.desiredObservingTimeMinutes) {
          setError('Time-based observations require both minimum and desired observing times');
          window.scrollTo(0, 0);
          return;
        }
        observationData.minimum_observing_time_minutes = parseInt(data.minimumObservingTimeMinutes);
        observationData.desired_observing_time_minutes = parseInt(data.desiredObservingTimeMinutes);
        observationData.fill_time = data.fillTime || false;
      }
      
      // Add monitoring cadence fields if applicable (deprecated but kept for backward compatibility)
      if (data.observationType === 'monitoring') {
        if (!data.cadenceDays || !data.cadenceStartDate) {
          setError('Monitoring observations require cadence interval and start date');
          window.scrollTo(0, 0);
          return;
        }
        observationData.cadence_days = parseFloat(data.cadenceDays);
        observationData.cadence_start_date = data.cadenceStartDate.toISOString();
        if (data.cadenceEndDate) {
          observationData.cadence_end_date = data.cadenceEndDate.toISOString();
        }
        if (data.cadenceMaxObservations) {
          observationData.cadence_max_observations = parseInt(data.cadenceMaxObservations);
        }
      }

      // External storage destinations (Issue #413). Sent even when empty: an array tells the
      // server the user chose, so it doesn't add back an org default they unchecked (#1474).
      if (Array.isArray(data.externalStorageDestinations)) {
        observationData.external_storage_destinations = data.externalStorageDestinations;
      }

      // Add notification preferences for this observation
      if (data.notifyEmail || data.notifyPushover) {
        observationData.notifications = {
          email: data.notifyEmail || false,
          pushover: data.notifyPushover || false,
          states: data.notifyStates || []
        };
      }

      // Add calibration options if overridden
      if (data.calibrationEnabled === 'true') {
        observationData.calibration_enabled = true;
      } else if (data.calibrationEnabled === 'false') {
        observationData.calibration_enabled = false;
      }
      if (data.rotationMatchFlats && data.rotationMatchFlats !== '') {
        observationData.require_rotation_matched_flats = data.rotationMatchFlats;
      }

      // Check if this should be a repetitive observation
      if (data.isRepetitive) {
        // Repetitive observations support multiple targets
        // Keep the targets array as-is
        const repetitiveObsData = {
          ...observationData
        };

        // Build repetition config - start_date is optional and defaults on backend
        const repetitionConfig = {
          interval_days: data.repetitionIntervalDays !== undefined && data.repetitionIntervalDays !== null
            ? parseFloat(data.repetitionIntervalDays)
            : 0,
          interval_hours: data.repetitionIntervalHours !== undefined && data.repetitionIntervalHours !== null
            ? parseFloat(data.repetitionIntervalHours)
            : 0,
          window_hours: data.repetitionWindowHours !== undefined && data.repetitionWindowHours !== null
            ? parseFloat(data.repetitionWindowHours)
            : 2,
        };

        // Optional: add start_date if provided
        if (data.repetitionStartDate) {
          repetitionConfig.start_date = data.repetitionStartDate.toISOString();
        }

        if (data.repetitionEndDate) {
          repetitionConfig.end_date = data.repetitionEndDate.toISOString();
        }
        if (data.repetitionMaxExecutions) {
          repetitionConfig.max_executions = parseInt(data.repetitionMaxExecutions);
        }
        if (data.repetitionSkipIfDelayedHours) {
          repetitionConfig.skip_if_delayed_hours = parseFloat(data.repetitionSkipIfDelayedHours);
        }
        if (data.repetitionPriorityBoostPerDay) {
          repetitionConfig.priority_boost_per_day = parseFloat(data.repetitionPriorityBoostPerDay);
        }

        // Use the repetitive endpoint
        console.log('Creating repetitive observation:', JSON.stringify({ observation_data: repetitiveObsData, repetition_config: repetitionConfig }, null, 2));
        await apiService.createRepetitiveObservation(repetitiveObsData, repetitionConfig);
      } else {
        // Regular single observation
        console.log('Final observation data being sent:', JSON.stringify(observationData, null, 2));
        createResult = await apiService.createObservation(observationData);
      }
      
      setError(''); // Clear any errors

      // #1119: the observation was created either way, but if the server flagged the window as
      // running past dark time, HOLD navigation so the advisory is actually read rather than
      // flashing past on the way to the list.
      const messages = warningMessages(createResult);
      if (messages.length > 0) {
        setDarkTimeWarnings(messages);
        setSubmitting(false);
        window.scrollTo(0, 0);
        return;
      }

      // Navigate to the observations list page
      if (embedded && onSuccess) { onSuccess(); } else { navigate('/observations'); }
    } catch (err) {
      console.error('Failed to submit observation:', err);
      const errorMsg = err.response?.data?.error;
      const statusCode = err.response?.status;

      // Handle conflict errors (HTTP 409) with adjustment dialog
      if (statusCode === 409 && errorMsg?.code === 'OVERLAPPING_TIME_WINDOWS') {
        console.log('Conflict detected, showing adjustment dialog:', err.response.data);
        setConflictData(err.response.data);
        setPendingObservationData(observationData);
        setConflictDialogOpen(true);
        setError(''); // Clear any previous errors
        setSubmitting(false);
        return; // Don't show generic error
      }

      // Handle filter validation errors specially
      if (errorMsg?.code === 'INVALID_FILTERS') {
        const availableFilters = errorMsg.details?.available_filters || [];
        const invalidFilters = errorMsg.details?.invalid_filters || [];
        setError(
          `Filter validation error: The filters "${invalidFilters.join(', ')}" are not available on the observatory. ` +
          `Available filters are: ${availableFilters.join(', ')}. ` +
          `Please go back to the Exposure Plan step and select valid filters.`
        );
      } else if (typeof errorMsg === 'object' && errorMsg.details) {
        // Show server validation details (e.g. "interval_minutes (3) is less than minimum allowed value (5)")
        setError(`${errorMsg.message || 'Validation error'}: ${errorMsg.details}`);
      } else if (typeof errorMsg === 'object' && errorMsg.message) {
        setError(errorMsg.message);
      } else if (typeof errorMsg === 'string') {
        setError(errorMsg);
      } else {
        setError('Failed to submit observation');
      }
      // Go back to review step so user can navigate to fix the issue
      setActiveStep(steps.length - 1);
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
    }
  };

  const { control, formState: { errors }, watch, setValue } = methods;

  // Handle user accepting an adjustment option
  const handleAcceptAdjustment = async (adjustmentOption) => {
    console.log('User accepted adjustment:', adjustmentOption);

    // Close the dialog
    setConflictDialogOpen(false);

    // Apply the adjustment to the pending observation data
    if (!pendingObservationData) {
      console.error('No pending observation data to adjust');
      return;
    }

    const adjustedData = {
      ...pendingObservationData,
      constraints: {
        ...pendingObservationData.constraints,
        start_time: adjustmentOption.adjusted_window.start,
        end_time: adjustmentOption.adjusted_window.end,
      },
      desired_observing_time_minutes: adjustmentOption.adjusted_window.duration_minutes,
      estimated_duration_minutes: adjustmentOption.adjusted_window.duration_minutes,
    };

    console.log('Resubmitting with adjusted data:', adjustedData);

    // Resubmit with adjusted window
    setSubmitting(true);
    try {
      if (adjustedData.isRepetitive) {
        // Extract repetition config if repetitive
        const { observation_data, repetition_config } = adjustedData;
        await apiService.createRepetitiveObservation(observation_data, repetition_config);
      } else {
        await apiService.createObservation(adjustedData);
      }

      setError('');

      // Navigate to the observations list page
      if (embedded && onSuccess) { onSuccess(); } else { navigate('/observations'); }
    } catch (err) {
      console.error('Failed to submit adjusted observation:', err);
      const errorMsg = err.response?.data?.error;

      if (typeof errorMsg === 'object' && errorMsg.message) {
        setError(errorMsg.message);
      } else if (typeof errorMsg === 'string') {
        setError(errorMsg);
      } else {
        setError('Failed to submit observation with adjusted window');
      }
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
      setPendingObservationData(null);
      setConflictData(null);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <ProjectStep />;
      case 1:
        return (
          <UnifiedTargetStep
            control={control}
            errors={errors}
            observatoryLocation={observatoryLocation}
            availableFilters={availableFilters}
            availableReadoutModes={availableReadoutModes}
            defaultReadoutMode={defaultReadoutMode}
          />
        );
      case 2:
        return <ConstraintsStep control={control} errors={errors} watch={watch} setValue={setValue} />;
      case 3:
        return <RepetitionStep />;
      case 4:
        return <AutofocusStep observatoryDefaults={observatoriesWithInstruments[0]} />;
      case 5:
        return <GuidingStep observatories={allObservatories} />;
      case 6:
        return <ReviewStep watch={watch} setValue={setValue} onSubmit={methods.handleSubmit(onSubmit)} isSubmitting={submitting} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Submit Observations
        </Typography>

        <Stepper activeStep={activeStep} sx={{ my: 4 }} nonLinear>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                onClick={() => setActiveStep(index)}
                sx={{
                  cursor: 'pointer',
                  '&:hover .MuiStepLabel-label': { color: 'primary.main' },
                  '&:hover .MuiStepIcon-root': { color: 'primary.main' },
                  '& .Mui-active.MuiStepLabel-label': { color: 'primary.main' },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* #1119: after submit, the server's advisory holds navigation until acknowledged. */}
        <DarkTimeWarningAlert
          messages={darkTimeWarnings}
          onContinue={() => {
            setDarkTimeWarnings([]);
            if (embedded && onSuccess) { onSuccess(); } else { navigate('/observations'); }
          }}
          continueLabel="Got it — view observations"
        />
        {/* Live, while building the window: only when the submit advisory is not already showing. */}
        {darkTimeWarnings.length === 0 && (
          <DarkTimeWarningAlert messages={liveDarkTimeWarning ? [liveDarkTimeWarning] : []} />
        )}

        <FormProvider {...methods}>
          <form>
            {getStepContent(activeStep)}

            {/* Only show navigation buttons if not on review step */}
            {activeStep !== steps.length - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Box>
            )}

            {/* Show back button on review step */}
            {activeStep === steps.length - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
                <Button onClick={handleBack}>
                  Back
                </Button>
              </Box>
            )}
          </form>
        </FormProvider>
      </Paper>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictDialogOpen}
        onClose={() => {
          setConflictDialogOpen(false);
          setPendingObservationData(null);
          setConflictData(null);
        }}
        conflictData={conflictData}
        onAcceptAdjustment={handleAcceptAdjustment}
        targetName={pendingObservationData?.name || 'Unknown'}
      />
    </Container>
  );
};

export default ObservationSubmit;